import type { BookingPeriodKind } from './assetBookingPricingOptions';

/** End of the given local calendar day (23:59), as an ISO string. */
function endOfLocalCalendarDay(year: number, monthIndex: number, day: number): string {
  return new Date(year, monthIndex, day, 23, 59, 0, 0).toISOString();
}

/**
 * Add `count` booking periods of `kind` to `startIso`.
 *
 * `hour` is exact clock math. For calendar periods (day/week/month/year) the end is the
 * **inclusive** end of the last covered calendar day, regardless of the start time of day:
 *  - 1 day  → the picked day only (ends that day at 23:59)
 *  - 2 days → the picked day and the day after
 *  - 1 week → the picked day plus the following 6 days, etc.
 */
export function addBookingPeriods(
  startIso: string,
  kind: BookingPeriodKind,
  count: number,
): string {
  if (count < 1 || !Number.isFinite(count)) {
    return '';
  }
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) {
    return '';
  }
  const whole = Math.floor(count);
  const year = start.getFullYear();
  const monthIndex = start.getMonth();
  const day = start.getDate();

  switch (kind) {
    case 'hour':
      return new Date(start.getTime() + whole * 60 * 60 * 1000).toISOString();
    case 'day':
      return endOfLocalCalendarDay(year, monthIndex, day + (whole - 1));
    case 'week':
      return endOfLocalCalendarDay(year, monthIndex, day + whole * 7 - 1);
    case 'month': {
      // Last day of the inclusive span: the day before the same date `whole` months later.
      const boundary = new Date(year, monthIndex + whole, day);
      boundary.setDate(boundary.getDate() - 1);
      return endOfLocalCalendarDay(
        boundary.getFullYear(),
        boundary.getMonth(),
        boundary.getDate(),
      );
    }
    case 'year': {
      const boundary = new Date(year + whole, monthIndex, day);
      boundary.setDate(boundary.getDate() - 1);
      return endOfLocalCalendarDay(
        boundary.getFullYear(),
        boundary.getMonth(),
        boundary.getDate(),
      );
    }
    default: {
      const _exhaustive: never = kind;
      return String(_exhaustive);
    }
  }
}

export function bookingPeriodCountLabel(kind: BookingPeriodKind, count: number): string {
  const n = Math.max(1, Math.floor(count));
  const labels: Record<BookingPeriodKind, [string, string]> = {
    hour: ['hour', 'hours'],
    day: ['day', 'days'],
    week: ['week', 'weeks'],
    month: ['month', 'months'],
    year: ['year', 'years'],
  };
  const [singular, plural] = labels[kind];
  return `${n} ${n === 1 ? singular : plural}`;
}

/** Human-readable local preview for a booking instant. */
export function formatBookingInstantPreview(iso: unknown, includeTime = true): string {
  if (iso == null || typeof iso !== 'string' || !iso.trim()) {
    return '—';
  }
  const dt = new Date(iso.trim());
  if (Number.isNaN(dt.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    ...(includeTime ? { timeStyle: 'short' as const } : {}),
  }).format(dt);
}

export function computeBookingTotalPrice(unitPrice: number | null, periodCount: number): number | null {
  if (unitPrice === null || !Number.isFinite(unitPrice)) {
    return null;
  }
  const n = Math.max(1, Math.floor(periodCount));
  return unitPrice * n;
}
